/* =========================================================================
   sh-audio-control.js - SafeHaven Luxury Smoke
   UI half of the WCAG 1.4.2 audio control. Upload to assets/.
   The order-critical half (window.Audio + HTMLMediaElement.prototype.play
   interception) lives inline in snippets/sh-audio-control.liquid and has
   already run by the time this file executes. Read that file first.

   This file is safe to defer. It only:
     - wires the button to window.SafeHavenAudio.toggle()
     - reflects state into aria-pressed + a class
     - reveals the control ONLY once an ambient element has been captured
     - keeps other tabs in step via the storage event
     - (v65) reports the RewardUp launcher's size and a row/stack/solo
       decision to the CSS for the bottom-left dock - see "Bottom-left dock"
   If window.SafeHavenAudio is missing (snippet not installed, or an inline-script
   CSP blocked it) this file does nothing and the button stays hidden. That is the
   intended degradation: no dead button.
   ========================================================================= */
(function () {
  var A = window.SafeHavenAudio;
  var wrap = document.getElementById('sh-audio-ctl');
  var btn = document.getElementById('sh-audio-btn');
  if (!A || !wrap || !btn) { return; }

  /* Accessible name is the constant visible label "Mute music"; the state is
     carried by aria-pressed, per the ARIA toggle-button pattern. Keeping the name
     stable also satisfies SC 2.5.3 Label in Name without a second string to
     maintain. Sighted users get the state from the inverted fill + the slashed
     speaker glyph, which is a shape change and therefore survives forced-colors. */
  function render() {
    btn.setAttribute('aria-pressed', A.muted ? 'true' : 'false');
    btn.classList.toggle('is-muted', !!A.muted);
    if (A.detected && wrap.hasAttribute('hidden')) {
      wrap.removeAttribute('hidden');
      schedule();
    }
  }

  /* ---- Bottom-left dock (v65, 2026-09-30) ---------------------------------
     sh-audio-control.css does ALL the positioning; this only tells it what the
     page actually looks like, via three things on the wrapper:
       --sh-ru-w / --sh-ru-h  the RewardUp launcher's drawn size (its rect,
                              which includes the theme's scale(.767))
       data-dock              row | stack | solo
     row   = right of Rewards, same baseline, 10px gap (the normal case)
     stack = above Rewards, left edges aligned - only when the row would bring
             Mute within 10px of Ask Sage (narrow phones)
     solo  = no visible RewardUp launcher (app absent/slow, age gate up):
             Mute takes the Rewards slot
     Without this code the CSS fallback already lands within a pixel of the
     same answer; this keeps it right if the app ever changes its launcher. */
  var GAP = 10;
  var root = document.documentElement;
  var mode = '';
  var queued = 0;
  var waits = 0;

  function shown(el) {
    if (!el || !el.isConnected) { return false; }
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') { return false; }
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function animating(el) {
    try {
      var list = el.getAnimations ? el.getAnimations() : [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].playState === 'running') { return true; }
      }
    } catch (e) {}
    return false;
  }

  /* Ask Sage's button is built by sh-sage.js (load order vs this file is not
     guaranteed), hides while its panel is open, and could change width - so
     it is watched lazily, the first time layout() sees it. */
  var sageWatched = null;
  function watchSage() {
    var sage = document.getElementById('sh-sage-btn');
    if (!sage || sage === sageWatched) { return; }
    sageWatched = sage;
    try { new ResizeObserver(schedule).observe(sage); } catch (e) {}
    try { new MutationObserver(schedule).observe(sage, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] }); } catch (e) {}
  }

  function layout() {
    queued = 0;
    watchSage();
    /* Sage's panel open: on phones the CSS hides the whole dock under it, and
       its hiding would read as "no RewardUp" here. Hold the layout; closing the
       panel un-hides #sh-sage-btn, which the observers below turn into a call. */
    if (document.querySelector('#sh-sage-panel.is-open')) { return; }
    var ru = document.getElementById('RewardUp-widget-btn');
    var next;
    if (root.classList.contains('sh-ag-lock') || !shown(ru)) {
      next = 'solo';
    } else {
      /* Mid-animation rects are transient (the gate's reveal pop, the app's
         own slide), so retry shortly. Bounded: where frames never paint an
         animation never ends; after ~9s stop polling and leave the CSS
         fallback (exact for today's launcher) in charge. Any observer event
         starts a fresh try. */
      if (root.classList.contains('sh-ag-reveal') || animating(ru)) {
        if (waits++ < 20) { later(450); }
        return;
      }
      waits = 0;
      var r = ru.getBoundingClientRect();
      /* A launcher far bigger than a pill means the app is in some expanded
         state we have not seen; keep the last layout rather than chase it. */
      if (r.height > 90 || r.width > 320) { return; }
      wrap.style.setProperty('--sh-ru-w', r.width.toFixed(2) + 'px');
      wrap.style.setProperty('--sh-ru-h', r.height.toFixed(2) + 'px');
      next = 'row';
      var sage = document.getElementById('sh-sage-btn');
      if (shown(sage)) {
        var s = sage.getBoundingClientRect();
        var mw = btn.offsetWidth || 46;
        if (r.right + GAP + mw > s.left - GAP) { next = 'stack'; }
      } else if (mode === 'stack') {
        next = 'stack'; /* Sage hidden (its panel is open): keep the last call */
      }
    }
    if (next !== mode) {
      mode = next;
      wrap.setAttribute('data-dock', next);
    }
  }

  /* Every observer/event lands here: a fresh signal restarts the bounded wait. */
  function schedule() {
    waits = 0;
    if (queued) { return; }
    queued = 1;
    /* A timer, not requestAnimationFrame: a page that is visible but not
       painting (an embedded preview pane, some in-app browsers) never runs rAF,
       and the dock must still settle there. Measured 2026-09-30: in the Claude
       browser pane rAF did not fire in 1500ms. */
    window.setTimeout(layout, 30);
  }

  /* A retry that keeps the wait count (layout() clears queued when it runs). */
  function later(ms) {
    queued = 1;
    window.setTimeout(layout, ms);
  }

  var watched = null;
  function watchLauncher() {
    var ru = document.getElementById('RewardUp-widget-btn');
    if (!ru || ru === watched) { return !!ru; }
    watched = ru;
    try { new ResizeObserver(schedule).observe(ru); } catch (e) {}
    try { new MutationObserver(schedule).observe(ru, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] }); } catch (e) {}
    ru.addEventListener('transitionend', schedule);
    ru.addEventListener('animationend', schedule);
    schedule();
    return true;
  }

  /* RewardUp injects its launcher after load. Watch <body>'s direct children
     until it arrives (and again if the app ever re-creates it). */
  try {
    new MutationObserver(function () { if (!watched || !watched.isConnected) { watchLauncher(); } schedule(); })
      .observe(document.body, { childList: true });
  } catch (e) {}
  /* The age gate adds/removes sh-ag-lock / sh-ag-reveal on <html>. */
  try { new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['class'] }); } catch (e) {}
  try { new ResizeObserver(schedule).observe(btn); } catch (e) {}
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  watchLauncher();
  schedule();

  A.subscribe(render);

  btn.addEventListener('click', function () {
    A.toggle();
    /* render() also runs via the subscription; calling it here makes the state
       change synchronous with the activation for screen readers. */
    render();
  });

  /* Cross-tab: a visitor who silences the music in one tab should not be attacked
     by a second tab that was already open. */
  window.addEventListener('storage', function (e) {
    if (!e || e.key !== A.storageKey) { return; }
    A.sync();
    render();
  });

  render();
})();
